// ============================================
// COTIZACIONESLISTA.JS — CO-3 / V2: listado de cotizaciones (compartido)
// ============================================
// Prop opcional oportunidadId:
//   - con valor: modo EXPEDIENTE (filtra por ?oportunidad_id, NO agrupa).
//   - sin valor: modo GENERAL (todas) AGRUPADAS por expediente.
//
// V2: en modo general se agrupa por expediente (oportunidad). Cada grupo tiene
// encabezado con el folio del expediente (oportunidad_folio) + cliente/sucursal.
// Las cotizaciones sin expediente caen en un grupo aparte resaltado. El numero
// que se muestra (#1, #2, ...) es un CONSECUTIVO POR EXPEDIENTE calculado por
// creado_at (la mas antigua del expediente = #1), no el consecutivo global del
// folio. El folio COT-... global se conserva como referencia en chico.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Search, FileText, AlertTriangle, Folder, Copy, ChevronDown, Check, CheckCircle2, Clock, XCircle, Tag } from 'lucide-react';
import { tokens } from '../styles/tokens';
import {
  API_BASE_CLI, PP, COLOR_OSCURO, COLOR_GRIS, COLOR_GRIS_CLARO, COLOR_BORDE,
  estiloInput, estiloTarjeta,
} from './helpersClientes';
import SelectorMoneda, { useMoneda } from './SelectorMoneda';
import { useResponsive, useAnchoViewport } from '../hooks/useResponsive';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import ModalEscanearCotizacion from './ModalEscanearCotizacion';
import { useUsuario } from '../context/UsuarioContext';
import { authHeaders } from '../utils/storage';
// FIX-TOKEN-COTIZACIONES: la lista (GET /api/cotizaciones) exige sesion + finanzas
// desde CALENDARIO-E1 (1148684). Estas dos lecturas seguian saliendo sin token y
// el backend respondia 401 "Token faltante" a TODOS, incluida direccion. fetchAuth
// inyecta el Bearer y, si el token expiro, limpia sesion y manda al login.
import { fetchAuth } from '../utils/sesion';
import useFamilias from '../hooks/useFamilias';
import { etiquetaRubro } from './FiltroRubro';

const ESTADO_COLOR = {
  borrador:   { bg: '#f3f4f4', color: '#5e666e', label: 'Borrador' },
  enviada:    { bg: '#e6f1fb', color: '#2f5f9e', label: 'Enviada' },
  vista:      { bg: '#fffbe6', color: '#7a6e00', label: 'Vista' },
  autorizada: { bg: '#e8faf5', color: '#1D9E75', label: 'Autorizada' },
  rechazada:  { bg: '#fde8e8', color: '#e05c5c', label: 'Rechazada' },
  vencida:    { bg: '#fff3e8', color: '#9a3412', label: 'Vencida' },
};
// CO-ESTADOS: estados que SE PUEDEN elegir (vencida queda fuera: es automatica).
const ESTADOS_OPCIONES = [
  { id: 'borrador',   label: 'Borrador' },
  { id: 'enviada',    label: 'Enviada' },
  { id: 'autorizada', label: 'Autorizada' },
  { id: 'rechazada',  label: 'Rechazada' },
];

// CO-ESTADOS: chip de estado. Muestra estado_efectivo (con 'vencida' derivada). Si
// `editable`, el chip es un boton que abre un menu para cambiarlo (mismo PATCH + gating).
// Definido a nivel de modulo (no se remonta en cada render del padre).
//
// FIX overflow: el menu se renderiza via createPortal a document.body con position:fixed
// (mismo patron que BadgeEstadoEditable de EXP-LISTA-V2 en Expedientes.js). Asi NO lo
// recorta ningun ancestro con overflow:hidden (las tarjetas redondeadas de la pestana
// Cotizaciones del expediente). Se posiciona con getBoundingClientRect del chip.
const MENU_ESTADO_W = 180;

function ChipEstado({ c, editable, onCambiar }) {
  const [abierto, setAbierto] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [posicion, setPosicion] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const efectivo = c.estado_efectivo || c.estado || 'borrador';
  const est = ESTADO_COLOR[efectivo] || ESTADO_COLOR.borrador;

  const chipBase = {
    display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px',
    borderRadius: 99, background: est.bg, color: est.color, fontSize: 11,
    fontWeight: 700, whiteSpace: 'nowrap', fontFamily: PP,
  };

  // Posicion fixed a partir del rect del chip: justo debajo, alineado a la derecha,
  // con clamp para no salirse del viewport (movil) y volteo hacia arriba si no cabe.
  const calcularPos = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const alto = 200;
    const espacioAbajo = window.innerHeight - rect.bottom;
    const arriba = espacioAbajo < alto && rect.top > alto;
    let left = rect.right - MENU_ESTADO_W;
    left = Math.max(8, Math.min(left, window.innerWidth - MENU_ESTADO_W - 8));
    return {
      top: arriba ? rect.top - 4 : rect.bottom + 4,
      left,
      transform: arriba ? 'translateY(-100%)' : 'none',
    };
  }, []);

  // Click fuera cierra (revisa trigger + menu: estan en arboles distintos por el portal).
  useEffect(() => {
    if (!abierto) return;
    const onClickFuera = (e) => {
      const enTrigger = triggerRef.current && triggerRef.current.contains(e.target);
      const enMenu = menuRef.current && menuRef.current.contains(e.target);
      if (!enTrigger && !enMenu) setAbierto(false);
    };
    document.addEventListener('mousedown', onClickFuera);
    return () => document.removeEventListener('mousedown', onClickFuera);
  }, [abierto]);

  // Escape cierra.
  useEffect(() => {
    if (!abierto) return;
    const onKey = (e) => { if (e.key === 'Escape') setAbierto(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [abierto]);

  // Scroll (ventana o contenedor, por eso capture) y resize cierran: con position:fixed
  // el menu no debe quedar flotando desalineado al hacer scroll.
  useEffect(() => {
    if (!abierto) return;
    const cerrar = () => setAbierto(false);
    window.addEventListener('scroll', cerrar, true);
    window.addEventListener('resize', cerrar);
    return () => {
      window.removeEventListener('scroll', cerrar, true);
      window.removeEventListener('resize', cerrar);
    };
  }, [abierto]);

  if (!editable) {
    return <span style={chipBase}>{est.label}</span>;
  }

  const abrirCerrar = (e) => {
    e.stopPropagation();
    if (abierto) { setAbierto(false); return; }
    setPosicion(calcularPos());
    setAbierto(true);
  };

  const elegir = async (e, nuevo) => {
    e.stopPropagation();
    setAbierto(false);
    if (guardando) return;
    setGuardando(true);
    try { await onCambiar(c.id, nuevo); }
    finally { setGuardando(false); }
  };

  return (
    <span style={{ display: 'inline-block' }} onClick={(e) => e.stopPropagation()}>
      <button
        ref={triggerRef}
        type="button"
        onClick={abrirCerrar}
        disabled={guardando}
        aria-label="Cambiar estado"
        style={{ ...chipBase, border: 'none', cursor: guardando ? 'default' : 'pointer', opacity: guardando ? 0.6 : 1 }}
      >
        {est.label} <ChevronDown size={12} strokeWidth={2.5} />
      </button>
      {abierto && posicion && createPortal(
        <div
          ref={menuRef}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed', top: posicion.top, left: posicion.left, transform: posicion.transform,
            zIndex: 9999, background: '#fff', border: `1px solid ${COLOR_BORDE}`, borderRadius: 10,
            boxShadow: '0 10px 30px rgba(0,0,0,0.16)', width: MENU_ESTADO_W, overflow: 'hidden',
          }}
        >
          {ESTADOS_OPCIONES.map(o => {
            const activo = o.id === (c.estado || 'borrador');
            return (
              <button
                key={o.id}
                type="button"
                onClick={(e) => elegir(e, o.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                  padding: '10px 12px', border: 'none', background: activo ? tokens.surfaceSubtle : '#fff',
                  cursor: 'pointer', fontSize: 13, fontFamily: PP, color: COLOR_OSCURO,
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = tokens.surfaceSubtle; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = activo ? tokens.surfaceSubtle : '#fff'; }}
              >
                <span style={{ width: 16, display: 'inline-flex' }}>
                  {activo && <Check size={14} strokeWidth={2.5} color={tokens.primary} />}
                </span>
                {o.label}
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </span>
  );
}

function fmtFecha(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '—'; }
}

// Milisegundos de un ISO (0 si invalido) para ordenar por creacion.
function ms(iso) {
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : 0;
}

// ESPEJO-COTIZACIONES (2026-07-10): onCambio avisa al contenedor (panel del
// paso Cotizacion / ficha) cuando una accion directa cambia datos, para que
// refresque el flujo (p.ej. autorizar completa el paso solo).
export default function CotizacionesLista({ oportunidadId = null, onCambio = null }) {
  const { formatear } = useMoneda();
  const { isMobile } = useResponsive();
  const anchoVp = useAnchoViewport();
  const [escanerAbierto, setEscanerAbierto] = useState(false);
  const { usuario, puede, accesoTotal, permisosFetched } = useUsuario();
  const modoExpediente = oportunidadId != null;
  // Embebida en el panel del flujo o en la ficha, la columna real es angosta aunque
  // isMobile (ancho de VENTANA) diga escritorio: bajo 1100px la barra va en columna y
  // la lista usa tarjetas en vez de tabla (mismo umbral useAnchoViewport del proyecto;
  // caso buscador de 74px y tabla con scroll forzado a 768).
  const compacto = isMobile || (modoExpediente && anchoVp < 1100);
  // CO-ESTADOS: gating del cambio rapido de estado. Deny-by-default mientras cargan
  // permisos; direccion (accesoTotal) siempre puede.
  const puedeCambiarEstado = accesoTotal || (permisosFetched && puede('cotizaciones.cambiar-estado'));

  const [cotizaciones, setCotizaciones] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [busq, setBusq] = useState('');
  // COTIZADOR-DUPLICAR: id de la cotizacion que se esta duplicando (para feedback / evitar doble click).
  const [duplicandoId, setDuplicandoId] = useState(null);
  // TIEMPO-REAL: hay un cambio de estado en vuelo (pausa el refresco automatico).
  const [cambiandoEstado, setCambiandoEstado] = useState(false);

  // CO-3.D: al hacer clic se abre el previsualizador en modo editar (via evento global).
  const abrirEditar = useCallback((id) => {
    window.dispatchEvent(new CustomEvent('beep-editar-cotizacion', { detail: { cotizacionId: id } }));
  }, []);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const qs = modoExpediente ? `?oportunidad_id=${encodeURIComponent(oportunidadId)}` : '';
      const resp = await fetchAuth(`${API_BASE_CLI}/api/cotizaciones${qs}`);
      const r = await resp.json().catch(() => ({}));
      if (r.ok) setCotizaciones(Array.isArray(r.cotizaciones) ? r.cotizaciones : []);
      else setError(r.error || 'No se pudieron cargar las cotizaciones');
    } catch (e) {
      // Sesion expirada: fetchAuth ya limpio la sesion y redirige al login. No
      // pintamos error (seria un mensaje muerto sobre una pantalla que se va).
      if (e && e.code === 'SESION_EXPIRADA') return;
      setError('Sin conexion');
    } finally {
      setCargando(false);
    }
  }, [oportunidadId, modoExpediente]);

  useEffect(() => { cargar(); }, [cargar]);

  // TIEMPO-REAL FASE 1: recarga SILENCIOSA (mismo fetch que `cargar`, mismo oportunidadId
  // en modo expediente) que actualiza los datos SIN tocar el spinner ni el error -> sin
  // parpadeo. El spinner queda solo para la carga inicial.
  const recargaSilenciosa = useCallback(async () => {
    try {
      const qs = modoExpediente ? `?oportunidad_id=${encodeURIComponent(oportunidadId)}` : '';
      const resp = await fetchAuth(`${API_BASE_CLI}/api/cotizaciones${qs}`);
      const r = await resp.json().catch(() => ({}));
      if (r.ok) setCotizaciones(Array.isArray(r.cotizaciones) ? r.cotizaciones : []);
      // Sin setCargando / setError: el refresco no debe molestar; si falla, reintenta luego.
      // Si el token expiro, fetchAuth lanza SESION_EXPIRADA y ya mando al login.
    } catch (e) {
      // silencioso
    }
  }, [oportunidadId, modoExpediente]);

  // Refresco inteligente: al volver a la pestana/foco + cada 20s si esta visible. Se
  // PAUSA mientras hay una operacion del usuario en curso (duplicar o cambiar estado)
  // para no pisar su trabajo; se reanuda solo al terminar.
  useAutoRefresh(recargaSilenciosa, {
    intervaloMs: 20000,
    pausado: duplicandoId != null || cambiandoEstado,
  });

  // COTIZADOR-DUPLICAR: crea una copia (POST /:id/duplicar), refresca la lista con
  // la copia y la abre en el editor reusando el evento global beep-editar-cotizacion.
  const duplicar = useCallback(async (id) => {
    if (duplicandoId != null) return; // evita doble click / duplicar dos a la vez
    setDuplicandoId(id);
    try {
      const r = await fetch(`${API_BASE_CLI}/api/cotizaciones/${id}/duplicar`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      }).then(x => x.json());
      if (r && r.ok && (r.cotizacion_id || r.id)) {
        await cargar(); // refresca la lista para que aparezca la copia
        if (typeof onCambio === 'function') onCambio();
        // abre la copia en el editor (mismo mecanismo que el click de la fila)
        window.dispatchEvent(new CustomEvent('beep-editar-cotizacion', { detail: { cotizacionId: r.cotizacion_id || r.id } }));
      }
    } catch (e) {
      // si falla, la lista queda igual; no rompemos el listado
    } finally {
      setDuplicandoId(null);
    }
  }, [duplicandoId, cargar, onCambio]);

  // CO-ESTADOS: cambio rapido de estado desde el chip (PATCH /:id/estado). Actualiza
  // la fila en memoria con la respuesta (estado + estado_efectivo). Error -> alert
  // con el mensaje REAL del servidor (matriz de transiciones, rol, motivo, $0).
  // SPRINT DINERO 1: si la cotizacion esta AUTORIZADA, el backend exige motivo
  // (y rol direccion/gerente); se pide con un prompt sencillo antes de llamar.
  // La firma la pone el backend con el usuario del token (ya no se manda usuario_id).
  const cambiarEstado = useCallback(async (id, nuevo) => {
    const actual = cotizaciones.find(c => c.id === id);
    let motivo = null;
    if ((actual?.estado || '') === 'autorizada') {
      const m = window.prompt('Cotizacion AUTORIZADA. Indica el motivo del cambio (obligatorio, queda en el historial):');
      if (m == null) return; // cancelado por el usuario
      motivo = m.trim();
      if (!motivo) {
        window.alert('El motivo es obligatorio para modificar una cotizacion autorizada');
        return;
      }
    }
    setCambiandoEstado(true); // pausa el refresco automatico mientras dura el cambio
    try {
      const r = await fetch(`${API_BASE_CLI}/api/cotizaciones/${id}/estado`, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ estado: nuevo, motivo }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.ok && d.cotizacion) {
        setCotizaciones(prev => prev.map(c => c.id === id
          ? { ...c, estado: d.cotizacion.estado, estado_efectivo: d.cotizacion.estado_efectivo }
          : c));
        // ESPEJO: avisar al contenedor (el flujo puede avanzar al autorizar).
        if (typeof onCambio === 'function') onCambio();
      } else {
        window.alert(d.error || 'No se pudo cambiar el estado de la cotizacion');
      }
    } catch (e) {
      window.alert('No se pudo cambiar el estado de la cotizacion');
    } finally {
      setCambiandoEstado(false);
    }
  }, [cotizaciones, onCambio]);

  const filtradas = useMemo(() => {
    const q = busq.trim().toLowerCase();
    if (!q) return cotizaciones;
    return cotizaciones.filter(c =>
      (c.nombre || '').toLowerCase().includes(q) ||
      (c.folio || '').toLowerCase().includes(q) ||
      (c.oportunidad_folio || '').toLowerCase().includes(q) ||
      (c.cliente_nombre || '').toLowerCase().includes(q) ||
      (c.cliente_grupo || '').toLowerCase().includes(q) ||
      (c.sucursal || '').toLowerCase().includes(q) ||
      (c.vendedor_nombre || '').toLowerCase().includes(q)
    );
  }, [cotizaciones, busq]);

  // Agrupa por expediente y calcula el CONSECUTIVO POR EXPEDIENTE.
  // Dentro de cada grupo: orden ascendente por creado_at para numerar (1 = mas
  // antigua), y luego se presenta descendente (mas reciente arriba) con su numero.
  // Grupos con expediente primero (por la cotizacion mas reciente del grupo);
  // "Sin expediente" siempre al final.
  const grupos = useMemo(() => {
    const porId = new Map();
    for (const c of filtradas) {
      const gid = c.oportunidad_id != null ? `op_${c.oportunidad_id}` : '__sin__';
      if (!porId.has(gid)) {
        porId.set(gid, {
          key: gid,
          sinExpediente: c.oportunidad_id == null,
          oportunidad_id: c.oportunidad_id != null ? c.oportunidad_id : null,
          folio: c.oportunidad_folio || null,
          cliente: c.cliente_grupo || null,
          sucursal: c.sucursal || null,
          cots: [],
        });
      }
      porId.get(gid).cots.push(c);
    }
    const lista = [...porId.values()].map(g => {
      const asc = [...g.cots].sort((a, b) => ms(a.creado_at) - ms(b.creado_at));
      const numeroPorId = new Map();
      asc.forEach((c, i) => numeroPorId.set(c.id, i + 1));
      const cots = [...g.cots]
        .sort((a, b) => ms(b.creado_at) - ms(a.creado_at))
        .map(c => ({ ...c, numeroExpediente: numeroPorId.get(c.id) }));
      const recientes = Math.max(0, ...g.cots.map(c => ms(c.creado_at)));
      return { ...g, cots, recientes };
    });
    lista.sort((a, b) => {
      if (a.sinExpediente !== b.sinExpediente) return a.sinExpediente ? 1 : -1;
      return b.recientes - a.recientes;
    });
    return lista;
  }, [filtradas]);

  // En modo expediente todo es un solo grupo: lista plana ya numerada por expediente.
  const filasExpediente = useMemo(() => grupos.flatMap(g => g.cots), [grupos]);

  // RUBROS Paso 2 (2026-10-16, decision de Ricardo): dentro del expediente, con MAS DE UNA cotizacion
  // se agrupan por rubro (columna `familia`, deducida de las lineas o puesta a mano). Con una sola se
  // ve como siempre. Orden de los grupos: el de las 13 familias; "Sin rubro" al final.
  const familias = useFamilias();
  const gruposRubro = useMemo(() => {
    // Se decide por el TOTAL de cotizaciones del expediente, no por las que deja el buscador: asi la vista
    // no salta de agrupada a plana al escribir (observacion de logica-negocio, 2026-10-16).
    if (!modoExpediente || cotizaciones.length <= 1) return null;
    const m = new Map();
    for (const c of filasExpediente) {
      const k = c.familia || '__sin__';
      if (!m.has(k)) m.set(k, { key: k, familia: c.familia || null, cots: [] });
      m.get(k).cots.push(c);
    }
    // Familia que no este en la lista (o lista aun sin cargar): al final, antes de "Sin rubro", igual que el filtro.
    const orden = (k) => { if (k === '__sin__') return 999; const i = familias.findIndex(f => f.id === k); return i < 0 ? 900 : i; };
    return [...m.values()]
      .map(g => ({ ...g, etiqueta: g.familia ? etiquetaRubro(g.familia, familias) : 'Sin rubro' }))
      .sort((a, b) => orden(a.key) - orden(b.key));
  }, [modoExpediente, filasExpediente, cotizaciones.length, familias]);

  // ---- estilos compartidos ----
  const th = { fontSize: 11, fontWeight: 700, color: COLOR_GRIS_CLARO, textAlign: 'left', padding: '8px 12px', textTransform: 'uppercase', letterSpacing: 0.3 };
  const td = { fontSize: 13, color: COLOR_OSCURO, padding: '11px 12px', borderTop: `1px solid ${COLOR_BORDE}`, verticalAlign: 'middle' };
  const numTab = { fontVariantNumeric: 'tabular-nums' };

  // Chip del consecutivo por expediente (#N). danger -> sin expediente.
  const chipNumero = (n, danger) => (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      minWidth: 34, height: 28, padding: '0 9px', borderRadius: 8,
      background: danger ? tokens.dangerSoft : tokens.primary100,
      color: danger ? tokens.danger : tokens.primary,
      border: `1px solid ${danger ? tokens.danger : 'transparent'}`,
      fontSize: 13, fontWeight: 800, ...numTab,
    }}>#{n != null ? n : '—'}</span>
  );

  // ---- desktop: tabla de un conjunto de cotizaciones (ya numeradas) ----
  const TablaDesktop = ({ cots, danger }) => (
    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 620 }}>
      <thead>
        <tr>
          <th style={{ ...th, width: 64 }}>No.</th>
          <th style={th}>Cotizacion</th>
          <th style={th}>Vendedor</th>
          <th style={{ ...th, textAlign: 'right' }}>Total</th>
          <th style={th}>Vencimiento</th>
          <th style={th}>Estado</th>
          <th style={{ ...th, width: 52 }}></th>
        </tr>
      </thead>
      <tbody>
        {cots.map(c => (
          <tr
            key={c.id}
            onClick={() => abrirEditar(c.id)}
            style={{ cursor: 'pointer' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = tokens.surfaceSubtle; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <td style={td}>{chipNumero(c.numeroExpediente, danger)}</td>
            <td style={td}>
              <div style={{ fontWeight: 700, color: COLOR_OSCURO }}>{c.nombre || c.folio}</div>
              <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 }}>
                <FileText size={11} strokeWidth={2} /> {c.folio}
              </div>
            </td>
            <td style={{ ...td, fontSize: 12, color: COLOR_GRIS_CLARO }}>{c.vendedor_nombre || '—'}</td>
            {/* Importe de un expediente ajeno: el hueco se queda VACIO (mismo criterio que la
                columna Monto de la lista de Expedientes). */}
            <td style={{ ...td, textAlign: 'right', fontWeight: 700, ...numTab }}>{c.dinero_oculto || c.total == null ? '' : formatear(c.total, 'MXN')}</td>
            <td style={td}>{fmtFecha(c.vence_at)}</td>
            <td style={td}><ChipEstado c={c} editable={puedeCambiarEstado} onCambiar={cambiarEstado} /></td>
            <td style={{ ...td, textAlign: 'right' }}>
              <button
                onClick={(e) => { e.stopPropagation(); duplicar(c.id); }}
                disabled={duplicandoId === c.id}
                aria-label="Duplicar"
                title="Duplicar"
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 34, height: 34, borderRadius: 8, cursor: 'pointer',
                  border: `1px solid ${COLOR_BORDE}`, background: '#fff', color: COLOR_GRIS,
                  opacity: duplicandoId === c.id ? 0.5 : 1,
                }}
              ><Copy size={15} strokeWidth={2} /></button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  // ---- movil: tarjeta tactil de una cotizacion ----
  const TarjetaMovil = ({ c, danger }) => (
    <div
      onClick={() => abrirEditar(c.id)}
      style={{
        padding: 14, background: '#fff', borderTop: `1px solid ${COLOR_BORDE}`,
        cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 10,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {chipNumero(c.numeroExpediente, danger)}
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: COLOR_OSCURO }}>{c.nombre || c.folio}</div>
            <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 }}>
              <FileText size={11} strokeWidth={2} /> {c.folio}
            </div>
          </div>
        </div>
        <ChipEstado c={c} editable={puedeCambiarEstado} onCambiar={cambiarEstado} />
      </div>

      <div style={{ fontSize: 22, fontWeight: 800, color: COLOR_OSCURO, ...numTab }}>
        {c.dinero_oculto || c.total == null ? '' : formatear(c.total, 'MXN')}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', fontSize: 12, color: COLOR_GRIS_CLARO }}>
        <span>Vence: {fmtFecha(c.vence_at)}</span>
        {c.vendedor_nombre && <span>{c.vendedor_nombre}</span>}
      </div>

      <button
        onClick={(e) => { e.stopPropagation(); duplicar(c.id); }}
        disabled={duplicandoId === c.id}
        aria-label="Duplicar"
        style={{
          alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '10px 16px', borderRadius: 10, cursor: 'pointer', minHeight: 44,
          border: `1px solid ${COLOR_BORDE}`, background: tokens.surfaceSubtle, color: COLOR_OSCURO,
          fontSize: 14, fontWeight: 700, fontFamily: PP,
          opacity: duplicandoId === c.id ? 0.5 : 1,
        }}
      ><Copy size={16} strokeWidth={2} /> {duplicandoId === c.id ? 'Duplicando…' : 'Duplicar'}</button>
    </div>
  );

  // ---- encabezado de grupo (expediente) ----
  const EncabezadoGrupo = ({ g }) => {
    const danger = g.sinExpediente;
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
        padding: '12px 14px',
        background: danger ? tokens.dangerSoft : tokens.surfaceSubtle,
        borderBottom: `1px solid ${danger ? tokens.danger : COLOR_BORDE}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          {danger
            ? <AlertTriangle size={16} strokeWidth={2} color={tokens.danger} />
            : <Folder size={16} strokeWidth={2} color={tokens.primary} />}
          <div style={{ minWidth: 0 }}>
            <div style={{
              fontWeight: 800, fontSize: 14, color: danger ? tokens.danger : COLOR_OSCURO,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {danger ? 'Sin expediente' : (g.folio || 'Expediente')}
            </div>
            {!danger && (g.cliente || g.sucursal) && (
              <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {g.cliente || ''}{g.cliente && g.sucursal ? ' · ' : ''}{g.sucursal || ''}
              </div>
            )}
          </div>
        </div>
        <span style={{
          flexShrink: 0, fontSize: 11, fontWeight: 700, color: danger ? tokens.danger : COLOR_GRIS_CLARO,
          background: '#fff', border: `1px solid ${danger ? tokens.danger : COLOR_BORDE}`,
          borderRadius: 99, padding: '3px 10px', ...numTab,
        }}>
          {g.cots.length} {g.cots.length === 1 ? 'cotizacion' : 'cotizaciones'}
        </span>
      </div>
    );
  };

  // DASHBOARD-COTIZACIONES (2026-07-10): resumen por grupos de estado del
  // expediente (solo LECTURA: suma los totales que ya calcula el backend con
  // IVA; cero formulas nuevas). Usa estado_efectivo (vencida derivada).
  const resumenExp = modoExpediente ? (() => {
    const g = {
      aut: { n: 0, s: 0, oculto: false },
      pend: { n: 0, s: 0, oculto: false },
      muertas: { n: 0, s: 0, oculto: false },
    };
    for (const c of cotizaciones) {
      const ef = c.estado_efectivo || c.estado || 'borrador';
      // EXPEDIENTES-RESUMEN (2026-09-17): el servidor no manda el importe de expedientes ajenos.
      // Esa cotizacion SI se cuenta (existe y esta en ese estado) pero NO suma, y se marca el
      // grupo como incompleto: enseñar "$0" seria mentir sobre dinero que si existe.
      const oculto = !!c.dinero_oculto || c.total == null;
      const t = oculto ? 0 : (Number(c.total) || 0);
      const dest = ef === 'autorizada' ? g.aut : (ef === 'rechazada' || ef === 'vencida' ? g.muertas : g.pend);
      dest.n += 1; dest.s += t;
      if (oculto) dest.oculto = true;
    }
    return g;
  })() : null;

  return (
    <div style={{ fontFamily: PP, color: COLOR_GRIS }}>
      {/* DASHBOARD (modo expediente): 3 tarjetas compactas con conteo y suma */}
      {modoExpediente && !cargando && !error && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
          {[
            { k: 'aut', label: 'Autorizadas', Icono: CheckCircle2, color: '#1D9E75', bg: '#e8faf5', borde: '#b8ecd9', d: resumenExp.aut },
            { k: 'pend', label: 'Pendientes', Icono: Clock, color: '#2f5f9e', bg: '#e6f1fb', borde: '#c4dcf5', d: resumenExp.pend },
            { k: 'muertas', label: 'Rechazadas y vencidas', Icono: XCircle, color: '#9a3412', bg: '#fbf0ee', borde: '#f0d9d3', d: resumenExp.muertas },
          ].map(t => (
            <div key={t.k} style={{
              flex: '1 1 150px', minWidth: 0, background: t.bg, border: `1px solid ${t.borde}`,
              borderRadius: 12, padding: '10px 12px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <t.Icono size={14} color={t.color} style={{ flexShrink: 0 }} />
                <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: t.color }}>
                  {t.label}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 18, fontWeight: 800, color: t.color }}>{t.d.n}</span>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: t.color, opacity: 0.9, whiteSpace: 'nowrap' }}>
                  {/* Con importes ocultos, la suma se calla (no es "$0", es "no te toca verlo"). */}
                  {t.d.oculto && t.d.s === 0 ? '' : formatear(t.d.s)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Barra superior: buscador + escanear PDF */}
      <div style={{
        display: 'flex', gap: 10, marginBottom: 14, alignItems: 'center',
        flexDirection: compacto ? 'column' : 'row',
      }}>
        <div style={{ position: 'relative', flex: 1, width: '100%', maxWidth: compacto ? '100%' : 420 }}>
          <Search size={16} strokeWidth={2} style={{ position: 'absolute', left: 10, top: 11, color: COLOR_GRIS_CLARO }} />
          <input
            style={{ ...estiloInput, paddingLeft: 34, fontSize: 13 }}
            placeholder="Buscar por nombre, folio, expediente o cliente…"
            value={busq}
            onChange={(e) => setBusq(e.target.value)}
          />
        </div>
        <button
          onClick={() => setEscanerAbierto(true)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap',
            padding: '10px 16px', borderRadius: 10, cursor: 'pointer', minHeight: 44,
            border: 'none', background: '#185FA5', color: '#fff',
            fontSize: 13, fontWeight: 700, fontFamily: PP,
            width: compacto ? '100%' : 'auto', justifyContent: 'center',
          }}
        >📄 Escanear cotizacion (PDF)</button>
        {/* Solo MONEDA (MXN/USD): convierte la presentacion de los totales. NO toca el
            IVA (los totales ya vienen calculados con IVA del backend). */}
        <div style={{ width: compacto ? '100%' : 'auto', display: 'flex', justifyContent: compacto ? 'flex-start' : 'flex-end' }}>
          <SelectorMoneda compacto mostrarTC={false} />
        </div>
      </div>

      <ModalEscanearCotizacion
        abierto={escanerAbierto}
        onCerrar={() => setEscanerAbierto(false)}
        onCreada={(id) => { setEscanerAbierto(false); cargar(); if (typeof onCambio === 'function') onCambio(); if (id) abrirEditar(id); }}
      />

      {cargando && <div style={{ color: COLOR_GRIS_CLARO, fontSize: 13 }}>Cargando cotizaciones…</div>}
      {!cargando && error && <div style={{ ...estiloTarjeta, padding: 16, color: tokens.danger }}>{error}</div>}

      {!cargando && !error && filtradas.length === 0 && (
        <div style={{ ...estiloTarjeta, padding: 30, textAlign: 'center', color: COLOR_GRIS_CLARO, fontSize: 13 }}>
          {cotizaciones.length === 0 ? 'Aun no hay cotizaciones.' : 'Sin resultados para la busqueda.'}
        </div>
      )}

      {/* MODO EXPEDIENTE: una sola cotizacion se ve como siempre; con mas de una, agrupadas por rubro. */}
      {!cargando && !error && filtradas.length > 0 && modoExpediente && !gruposRubro && (
        <div style={{ ...estiloTarjeta, padding: 0, overflow: 'hidden' }}>
          {compacto
            ? filasExpediente.map(c => <TarjetaMovil key={c.id} c={c} danger={false} />)
            : <div style={{ overflowX: 'auto' }}><TablaDesktop cots={filasExpediente} danger={false} /></div>}
        </div>
      )}
      {!cargando && !error && filtradas.length > 0 && modoExpediente && gruposRubro && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {gruposRubro.map(g => (
            <div key={g.key} style={{ ...estiloTarjeta, padding: 0, overflow: 'hidden' }}>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                padding: '10px 14px', background: tokens.surfaceSubtle, borderBottom: `1px solid ${COLOR_BORDE}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                  <Tag size={15} strokeWidth={2} color={g.familia ? tokens.primary : COLOR_GRIS_CLARO} />
                  <span style={{ fontWeight: 800, fontSize: 13.5, color: g.familia ? COLOR_OSCURO : COLOR_GRIS_CLARO, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {g.etiqueta}
                  </span>
                </div>
                <span style={{ flexShrink: 0, fontSize: 11, fontWeight: 700, color: COLOR_GRIS_CLARO, background: '#fff', border: `1px solid ${COLOR_BORDE}`, borderRadius: 99, padding: '3px 10px', ...numTab }}>
                  {g.cots.length} {g.cots.length === 1 ? 'cotizacion' : 'cotizaciones'}
                </span>
              </div>
              {compacto
                ? g.cots.map(c => <TarjetaMovil key={c.id} c={c} danger={false} />)
                : <div style={{ overflowX: 'auto' }}><TablaDesktop cots={g.cots} danger={false} /></div>}
            </div>
          ))}
        </div>
      )}

      {/* MODO GENERAL: agrupado por expediente. */}
      {!cargando && !error && filtradas.length > 0 && !modoExpediente && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {grupos.map(g => (
            <div key={g.key} style={{
              ...estiloTarjeta, padding: 0, overflow: 'hidden',
              border: `1px solid ${g.sinExpediente ? tokens.danger : COLOR_BORDE}`,
            }}>
              <EncabezadoGrupo g={g} />
              {compacto
                ? g.cots.map(c => <TarjetaMovil key={c.id} c={c} danger={g.sinExpediente} />)
                : <div style={{ overflowX: 'auto' }}><TablaDesktop cots={g.cots} danger={g.sinExpediente} /></div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
